import { useMemo, useRef, useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogFooter,
  Button,
  Separator,
  Label,
  Textarea,
} from '@/components/ui'
import type { MenuItem, ComboGroup } from '@/types/menu'
import type { ComboSelections } from '@/types/cart'
import { useAppDispatch } from '@/store/hooks'
import { addItem } from '@/store/cartSlice'
import { isRowVisible, isIngredientEnabled, getSelectionLimit } from '@/utils/ruleEngine'
import { formatPrice } from '@/utils/formatting'

import { ItemHeader } from './ItemHeader'
import { ModifierGroup } from './ModifierGroup'
import { ComboMainMeal } from './ComboMainMeal'
import { ComboGroupSelector } from './ComboGroupSelector'
import { QuantitySelector } from './QuantitySelector'
import type { ModifierOption, ModifierOptionVariation } from '@/types/modifier'
import { ChevronLeft, X } from 'lucide-react'

import {
  useItemCustomization,
  useRuleEvaluation,
  usePriceCalculation,
} from '@/hooks/customization'

// Helper: count total quantity for a group (sum of quantity fields, defaulting to 1)
function countGroupQty(mods: { groupId: string; quantity?: number }[], groupId: string) {
  return mods.filter((m) => m.groupId === groupId).reduce((sum, m) => sum + (m.quantity ?? 1), 0)
}

interface ItemCustomizationModalProps {
  item: MenuItem | null
  isOpen: boolean
  onClose: () => void
}

export function ItemCustomizationModal({
  item,
  isOpen,
  onClose,
}: ItemCustomizationModalProps) {
  const dispatch = useAppDispatch()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Page navigation state
  const [currentPage, setCurrentPage] = useState<'main' | 'variation'>('main')
  const [variationPageData, setVariationPageData] = useState<{
    groupId: string
    groupName: string
    option: ModifierOption
  } | null>(null)
  const [tempSelectedVariation, setTempSelectedVariation] = useState<ModifierOptionVariation | null>(null)

  // State management
  const {
    quantity,
    setQuantity,
    selectedModifiers,
    setSelectedModifiers,
    mainMealCustomizations,
    setMainMealCustomizations,
    comboGroupSelections,
    setComboGroupSelections,
    ingredientVariationSelections,
    setIngredientVariationSelections,
    specialInstructions,
    setSpecialInstructions,
    mainMealExpanded,
    setMainMealExpanded,
  } = useItemCustomization(item)

  // Rule evaluation
  const {
    singleItemRuleResult,
    mainMealRuleResult,
    getGroupOptionsToDisplay,
    getRowIngredientsToDisplay,
  } = useRuleEvaluation(
    item,
    selectedModifiers,
    mainMealCustomizations,
    setSelectedModifiers,
    setMainMealCustomizations
  )

  // Price calculation
  const { totalPrice } = usePriceCalculation(
    item,
    quantity,
    selectedModifiers,
    comboGroupSelections,
    mainMealCustomizations
  )

  // Handle modal close
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCurrentPage('main')
      setVariationPageData(null)
      setTempSelectedVariation(null)
      onClose()
    }
  }

  // Reset page when item changes
  useEffect(() => {
    setCurrentPage('main')
    setVariationPageData(null)
    setTempSelectedVariation(null)
  }, [item])

  // Handle modifier toggle
  const handleModifierToggle = (
    groupId: string,
    groupName: string,
    optionId: string,
    optionName: string,
    price: number,
    variationId?: string,
    variationName?: string
  ) => {
    // Find the group to get maxSelections
    const group = item?.modifierGroups?.find((g) => g.id === groupId)
    const maxSelections = group
      ? getSelectionLimit(groupId, singleItemRuleResult, group.maxSelections || group.options.length)
      : undefined

    setSelectedModifiers((prev) => {
      // Single-select (radio): always replace the whole group
      if (maxSelections === 1) {
        const rest = prev.filter((m) => m.groupId !== groupId)
        return [
          ...rest,
          { groupId, groupName, optionId, optionName, priceAdjustment: price, variationId, variationName },
        ]
      }

      // Multi-select: check if updating variation for existing selection
      const exists = prev.find((m) => m.groupId === groupId && m.optionId === optionId)

      // If variationId provided and item exists, UPDATE the variation instead of removing
      if (exists && variationId) {
        return prev.map((m) =>
          m.groupId === groupId && m.optionId === optionId
            ? { ...m, variationId, variationName, priceAdjustment: price }
            : m
        )
      }

      // Normal toggle logic: remove if exists
      if (exists) {
        return prev.filter((m) => !(m.groupId === groupId && m.optionId === optionId))
      }

      // Add new selection if under limit
      const currentCount = prev.filter((m) => m.groupId === groupId).length
      if (maxSelections && currentCount >= maxSelections) {
        return prev
      }
      return [
        ...prev,
        { groupId, groupName, optionId, optionName, priceAdjustment: price, variationId, variationName },
      ]
    })
  }

  // Handle opening variation page for an ingredient option
  const handleOpenVariationPage = (groupId: string, groupName: string, option: ModifierOption) => {
    // Set currently selected variation as temp selection
    const currentSelection = selectedModifiers.find(
      (m) => m.groupId === groupId && m.optionId === option.id
    )
    if (currentSelection?.variationId && option.variationGroup) {
      const variation = option.variationGroup.variations.find((v) => v.id === currentSelection.variationId)
      setTempSelectedVariation(variation || null)
    } else {
      // Default to default variation
      const defaultVar = option.variationGroup?.variations.find((v) => v.is_default)
      setTempSelectedVariation(defaultVar || null)
    }
    setVariationPageData({ groupId, groupName, option })
    setCurrentPage('variation')
  }

  // Handle clicking a variation (just select, don't navigate)
  const handleClickVariation = (variation: ModifierOptionVariation) => {
    setTempSelectedVariation(variation)
  }

  // Handle confirming variation selection
  const handleConfirmVariation = () => {
    if (!variationPageData || !tempSelectedVariation) return

    const { groupId, groupName, option } = variationPageData

    // Use the variation's price if available, otherwise fall back to option's base price
    const price = tempSelectedVariation.price !== undefined
      ? tempSelectedVariation.price
      : (option.priceAdjustment || 0)

    // Apply the variation to the ingredient
    handleModifierToggle(
      groupId,
      groupName,
      option.id,
      option.name,
      price,
      tempSelectedVariation.id,
      tempSelectedVariation.name
    )

    // Return to main page
    setCurrentPage('main')
    setVariationPageData(null)
    setTempSelectedVariation(null)
  }

  // Handle back from variation page
  const handleBackFromVariation = () => {
    setCurrentPage('main')
    setVariationPageData(null)
    setTempSelectedVariation(null)
  }

  // Handle quantity-mode modifier change
  const handleQuantityModifierChange = (
    groupId: string,
    groupName: string,
    optionId: string,
    optionName: string,
    price: number,
    newQty: number
  ) => {
    setSelectedModifiers((prev) => {
      const rest = prev.filter((m) => !(m.groupId === groupId && m.optionId === optionId))
      if (newQty <= 0) return rest
      return [
        ...rest,
        { groupId, groupName, optionId, optionName, priceAdjustment: price, quantity: newQty },
      ]
    })
  }

  // Handle combo group option toggle (multi-select)
  const handleComboGroupToggle = (group: ComboGroup, optionId: string) => {
    const option = group.options.find((o) => o.id === optionId)
    if (!option) return

    setComboGroupSelections((prev) => {
      const isSelected = prev.some((s) => s.groupId === group.id && s.optionId === optionId)

      if (isSelected) {
        return prev.filter((s) => !(s.groupId === group.id && s.optionId === optionId))
      } else {
        const currentCount = prev.filter((s) => s.groupId === group.id).length
        if (currentCount >= group.maxSelections) {
          return prev
        }
        return [
          ...prev,
          {
            groupId: group.id,
            groupName: group.name,
            optionId: option.id,
            optionName: option.ingredient.name,
            additionalPrice: option.additionalPrice,
          },
        ]
      }
    })
  }

  // Validate selections before adding to cart
  const canAddToCart = useMemo(() => {
    if (!item) return false

    // For single items: check required modifier groups (only visible ones)
    if (item.itemType === 'single' && item.modifierGroups) {
      const visibleGroups = item.modifierGroups.filter((g) =>
        isRowVisible(g.id, singleItemRuleResult, true)
      )
      const requiredGroups = visibleGroups.filter((g) => g.required)
      for (const group of requiredGroups) {
        const hasSelection = selectedModifiers.some((m) => m.groupId === group.id)
        if (!hasSelection) return false
      }

      // Check min/max selections for multi-select, quantity, AND single groups
      for (const group of visibleGroups) {
        const totalQty = countGroupQty(selectedModifiers, group.id)
        if (group.minSelections && totalQty < group.minSelections) return false
        if (group.maxSelections && totalQty > group.maxSelections) return false
      }
    }

    // For combo items: check minimum selections are met for each group
    if (item.itemType === 'combo' && item.comboProduct?.groups) {
      for (const group of item.comboProduct.groups) {
        if (group.minSelections > 0) {
          const selectionCount = comboGroupSelections.filter((s) => s.groupId === group.id).length
          if (selectionCount < group.minSelections) return false
        }
      }
    }

    // For combo items: check main meal row minSelections
    if (item.itemType === 'combo' && item.comboProduct?.mainMeal?.rows) {
      for (const row of item.comboProduct.mainMeal.rows) {
        if (row.minSelections > 0) {
          const totalQty = countGroupQty(mainMealCustomizations, row.id)
          if (totalQty < row.minSelections) return false
        }
      }
    }

    return true
  }, [item, selectedModifiers, comboGroupSelections, mainMealCustomizations, singleItemRuleResult])

  // Compute remaining required selections
  const remainingRequired = useMemo(() => {
    if (!item) return 0
    let remaining = 0

    if (item.itemType === 'single' && item.modifierGroups) {
      const visibleGroups = item.modifierGroups.filter((g) =>
        isRowVisible(g.id, singleItemRuleResult, true)
      )
      const requiredGroups = visibleGroups.filter((g) => g.required)
      for (const group of requiredGroups) {
        const hasSelection = selectedModifiers.some((m) => m.groupId === group.id)
        if (!hasSelection) remaining += 1
      }
      for (const group of visibleGroups) {
        if ((group.minSelections || 0) > 0) {
          const totalQty = countGroupQty(selectedModifiers, group.id)
          if (totalQty < (group.minSelections || 0)) {
            remaining += (group.minSelections || 0) - totalQty
          }
        }
      }
    }

    if (item.itemType === 'combo' && item.comboProduct?.groups) {
      for (const group of item.comboProduct.groups) {
        if (group.minSelections > 0) {
          const selectionCount = comboGroupSelections.filter((s) => s.groupId === group.id).length
          if (selectionCount < group.minSelections) {
            remaining += group.minSelections - selectionCount
          }
        }
      }
    }

    if (item.itemType === 'combo' && item.comboProduct?.mainMeal?.rows) {
      for (const row of item.comboProduct.mainMeal.rows) {
        if (row.minSelections > 0) {
          const totalQty = countGroupQty(mainMealCustomizations, row.id)
          if (totalQty < row.minSelections) {
            remaining += row.minSelections - totalQty
          }
        }
      }
    }

    return remaining
  }, [item, selectedModifiers, comboGroupSelections, mainMealCustomizations, singleItemRuleResult])

  const handleAddToCart = () => {
    if (!item || !canAddToCart) return

    // If a variation with its own price is selected, override menuItem.price
    let menuItemForCart = item
    if (item.ingredientVariationGroup) {
      const selMod = selectedModifiers.find((m) => m.groupId === item.ingredientVariationGroup!.id)
      if (selMod) {
        const selVar = item.ingredientVariationGroup.variations.find((v) => v.id === selMod.optionId)
        if (selVar?.price !== undefined) {
          menuItemForCart = { ...item, price: selVar.price }
        }
      }
    }

    // Build combo selections for cart
    let comboSelectionsPayload: ComboSelections | undefined
    if (item.itemType === 'combo' && item.comboProduct) {
      comboSelectionsPayload = {
        mainMealCustomizations:
          mainMealCustomizations.length > 0 ? mainMealCustomizations : undefined,
        groupSelections: comboGroupSelections,
      }
    }

    dispatch(
      addItem({
        menuItem: menuItemForCart,
        selectedModifiers,
        quantity,
        specialInstructions: specialInstructions || undefined,
        comboSelections: comboSelectionsPayload,
      })
    )

    handleOpenChange(false)
  }

  if (!item) return null

  const hasComboProduct = item.itemType === 'combo' && item.comboProduct
  const mainMeal = item.comboProduct?.mainMeal
  const comboGroups = item.comboProduct?.groups || []

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col !p-0 gap-0 overflow-hidden" hideCloseButton>
        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide overscroll-contain" ref={scrollContainerRef}>
          {/* Floating close — sticky, zero-height so image starts at top */}
          <div className="sticky top-0 z-20 pointer-events-none h-0 overflow-visible">
            <div className="flex justify-end p-3">
              <DialogClose className="pointer-events-auto w-10 h-10 rounded-full bg-black/30 dark:bg-white/20 backdrop-blur-xl text-white shadow-lg flex items-center justify-center cursor-pointer hover:bg-black/50 dark:hover:bg-white/30 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
            </div>
          </div>

          {currentPage === 'main' ? (
            <>
              {/* Hero image — immersive, cinematic gradient blend into content */}
              {item.image_url && (
                <div className="relative w-full aspect-[16/10] bg-muted overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                </div>
              )}

              {/* Content — emerges from image gradient */}
              <div className={`relative px-5 sm:px-6 pb-6 ${item.image_url ? '-mt-8 pt-4' : 'pt-16'}`}>
                <ItemHeader item={item} />

              {/* Ingredient Variations (size, etc.) */}
              {item.ingredientVariationGroup && (
                <>
                  <Separator className="my-5" />
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">{item.ingredientVariationGroup.name}</Label>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        const variations = item.ingredientVariationGroup.variations
                          .slice()
                          .sort((a, b) => a.sort_order - b.sort_order)

                        // Find default variation price for difference calculation
                        const defaultVariation = variations.find((v) => v.is_default)
                        const defaultPrice = defaultVariation?.price ?? item.price

                        return variations.map((v) => {
                          const isSelected = selectedModifiers.some(
                            (m) => m.groupId === item.ingredientVariationGroup!.id && m.optionId === v.id
                          )

                          // Calculate price difference from default
                          let priceLabel = ''
                          if (v.price !== undefined && defaultPrice !== undefined) {
                            const diff = v.price - defaultPrice
                            if (diff > 0) {
                              priceLabel = ` (+${formatPrice(diff, item.currency, false)})`
                            } else if (diff < 0) {
                              priceLabel = ` (-${formatPrice(Math.abs(diff), item.currency, false)})`
                            }
                          }

                          return (
                            <Button
                              key={v.id}
                              variant={isSelected ? 'default' : 'secondary'}
                              className="px-4 py-2.5 h-auto rounded-full text-sm"
                              onClick={() => {
                                setSelectedModifiers((prev) => [
                                  ...prev.filter((m) => m.groupId !== item.ingredientVariationGroup!.id),
                                  {
                                    groupId: item.ingredientVariationGroup!.id,
                                    groupName: item.ingredientVariationGroup!.name,
                                    optionId: v.id,
                                    optionName: v.name,
                                    priceAdjustment: 0,
                                  },
                                ])
                              }}
                            >
                              {v.name}{priceLabel}
                            </Button>
                          )
                        })
                      })()}
                    </div>
                  </div>
                </>
              )}

              <Separator className="my-5" />

              {/* Single Item: Modifiers */}
              {item.itemType === 'single' && item.modifierGroups && item.modifierGroups.length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Customize Your Item</h3>

                  {item.modifierGroups
                    .filter((group) => isRowVisible(group.id, singleItemRuleResult, true))
                    .map((group) => {
                      const displayOptions = getGroupOptionsToDisplay(group)
                      const selectedInGroup = selectedModifiers.filter((m) => m.groupId === group.id)
                      const maxSelections = getSelectionLimit(
                        group.id,
                        singleItemRuleResult,
                        group.maxSelections || displayOptions.length
                      )

                      return (
                        <div key={group.id} ref={(el) => { groupRefs.current[group.id] = el }}>
                          <ModifierGroup
                            groupId={group.id}
                            groupName={group.name}
                            selectionType={group.selectionType}
                            options={displayOptions}
                            selectedOptions={selectedInGroup}
                            maxSelections={maxSelections}
                            minSelections={group.minSelections}
                            maxQuantityPerItem={group.maxQuantityPerItem}
                            onToggle={handleModifierToggle}
                            onQuantityChange={handleQuantityModifierChange}
                            isIngredientEnabled={(optionId) =>
                              isIngredientEnabled(group.id, optionId, singleItemRuleResult, true)
                            }
                            onOpenVariationPicker={(option) =>
                              handleOpenVariationPage(group.id, group.name, option)
                            }
                            menuItem={item}
                          />
                        </div>
                      )
                    })}
                </div>
              )}

              {/* Combo Item: Main Meal + Groups */}
              {hasComboProduct && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Build Your Combo</h3>

                  {/* Main Meal Section */}
                  {mainMeal && (
                    <ComboMainMeal
                      mainMeal={mainMeal}
                      customizations={mainMealCustomizations}
                      onCustomizationChange={setMainMealCustomizations}
                      ingredientVariationSelections={ingredientVariationSelections}
                      onIngredientVariationChange={setIngredientVariationSelections}
                      ruleResult={mainMealRuleResult}
                      getIngredientsToDisplay={getRowIngredientsToDisplay}
                      expanded={mainMealExpanded}
                      onExpandedChange={setMainMealExpanded}
                      menuItem={item}
                    />
                  )}

                  {/* Combo Groups (Drink, Side, etc.) */}
                  {comboGroups.length > 0 && (
                    <div className="space-y-6">
                      {[...comboGroups]
                        .sort((a, b) => a.order - b.order)
                        .map((group) => (
                          <div key={group.id} ref={(el) => { groupRefs.current[group.id] = el }}>
                            <ComboGroupSelector
                              group={group}
                              selectedOptions={comboGroupSelections.filter((s) => s.groupId === group.id)}
                              onToggle={handleComboGroupToggle}
                            />
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              <Separator className="my-5" />

              {/* Special Instructions */}
              <div className="space-y-3">
                <Label htmlFor="instructions" className="text-base font-semibold">
                  Special Instructions (Optional)
                </Label>
                <Textarea
                  id="instructions"
                  placeholder="Any special requests? (e.g., no onions, extra sauce)"
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  rows={3}
                />
              </div>

              <Separator className="my-5" />

          {/* Quantity Selector */}
          <QuantitySelector quantity={quantity} onQuantityChange={setQuantity} />
              </div>
            </>
          ) : (
            <div className="px-5 sm:px-6 pt-16 pb-4">
              {/* Variation Selection Page */}
              {variationPageData && (
                <div className="space-y-4">
                  {/* Back Header */}
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleBackFromVariation}
                      className="w-11 h-11"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                      <h3 className="font-semibold text-lg">{variationPageData.option.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Select {variationPageData.option.variationGroup?.name}
                      </p>
                    </div>
                  </div>

                  {/* Variations List */}
                  <div className="space-y-2">
                    {(() => {
                      const variations = variationPageData.option.variationGroup?.variations || []

                      return variations
                        .slice()
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((variation) => {
                          const isSelected = tempSelectedVariation?.id === variation.id

                          // Calculate price difference from default
                          const defaultVariation = variations.find((v) => v.is_default)
                          const defaultPrice = defaultVariation?.price
                          let priceLabel = ''
                          if (variation.price !== undefined && defaultPrice !== undefined) {
                            const diff = variation.price - defaultPrice
                            if (diff > 0) {
                              priceLabel = `+${formatPrice(diff, item.currency, false)}`
                            } else if (diff < 0) {
                              priceLabel = `-${formatPrice(Math.abs(diff), item.currency, false)}`
                            }
                          }

                        return (
                          <Button
                            key={variation.id}
                            variant="outline"
                            className={`w-full flex items-center justify-between p-4 h-auto rounded-xl border-2 text-left ${
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                            }`}
                            onClick={() => handleClickVariation(variation)}
                          >
                            <div className="flex items-center gap-3">
                              {/* Radio circle */}
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                  isSelected ? 'border-primary' : 'border-muted-foreground/30'
                                }`}
                              >
                                {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                              </div>

                              {/* Name */}
                              <div>
                                <p className={`font-medium ${isSelected ? 'text-primary' : ''}`}>
                                  {variation.name}
                                </p>
                                {priceLabel && (
                                  <p className="text-sm text-muted-foreground font-normal">{priceLabel}</p>
                                )}
                              </div>
                            </div>

                            {/* Absolute price if available */}
                            {variation.price !== undefined ? (
                              <span className="font-semibold text-sm">{formatPrice(variation.price, item.currency, false)}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground italic font-normal">No price</span>
                            )}
                          </Button>
                        )
                      })
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — elevated with subtle shadow */}
        <DialogFooter className="shrink-0 flex-col sm:flex-col gap-3 px-5 sm:px-6 py-4 bg-background border-t border-border shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
          {currentPage === 'main' ? (
            <Button
              onClick={handleAddToCart}
              disabled={!canAddToCart}
              size="lg"
              className="w-full text-lg h-14 rounded-xl font-semibold"
              variant="black"
            >
              {canAddToCart
                ? `Add to Cart • ${formatPrice(totalPrice, item.currency, false)}`
                : `Make ${remainingRequired} required selection${remainingRequired !== 1 ? 's' : ''} • ${formatPrice(totalPrice, item.currency, false)}`}
            </Button>
          ) : (
            <Button
              onClick={handleConfirmVariation}
              disabled={!tempSelectedVariation}
              size="lg"
              className="w-full text-lg h-14 rounded-xl font-semibold"
              variant="black"
            >
              Select Variation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
